import Link from '@BlackbirdHQ/ui-base/link';
import {
  Build as BuildIcon,
  Done as DoneIcon,
  HelpOutline,
  Report as ReportIcon,
  RingVolume,
} from '@mui/icons-material';
import { Avatar, Button, Card, CardContent, Grid, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { amber, green, red } from '@mui/material/colors';
import dynamic from 'next/dynamic';
import { FunctionComponent, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import ScrollButton from '@/components/buttons/scroll-button';
import HelpDialogStepper, { HelpSteps } from '@/components/help/help-dialog-stepper';
import ContextTimepickerWrapper from '@/contexts/timepicker';
import { useURLQuery } from '@/hooks';
import CallsOverview from '@/views/andon/tabs/calls';

const Schedules = dynamic(async () => import('@/views/andon/tabs/schedules'));
const Workers = dynamic(async () => import('@/views/andon/tabs/workers'));

const useStyles = makeStyles()((theme) => ({
  avatar: {
    margin: 10,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 1,
    marginRight: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(),
    },
  },
  headerTabs: {
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      order: 10,
    },
    '& > div > div > div::-webkit-scrollbar': {
      display: 'none',
    },
    '& > div > div > div': {
      overflow: '-moz-scrollbars-none',
      '-ms-overflow-style': 'none',
      'scrollbar-width': 'none',
    },
  },
  tab: {
    minWidth: '150px',
    textAlign: 'center',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  indicators: {
    display: 'flex',
    flexShrink: 1,
    maxWidth: '20%',
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '25%',
      flexBasis: '25%',
    },
  },
  '@keyframes pulseUrgencyCritical': {
    '0%': { backgroundColor: red['800'] },
    '50%': { backgroundColor: red['200'] },
    '100%': { backgroundColor: red['800'] },
  },
  '@keyframes pulseUrgencyMedium': {
    '0%': { backgroundColor: amber['600'] },
    '50%': { backgroundColor: amber['400'] },
    '100%': { backgroundColor: amber['600'] },
  },
  littlePadding: {
    [theme.breakpoints.down('md')]: {
      '& > *:first-child': {
        paddingLeft: theme.spacing(),
        paddingRight: theme.spacing(0.5),
      },
      '& > *, & > *:last-child': {
        paddingRight: theme.spacing(),
      },
    },
  },
  buttonMargin: {
    marginRight: theme.spacing(),
    margin: theme.spacing(0.5),
  },
  buttonInProgress: {
    animationDuration: '3s !important',
  },
  buttonUrgencyCritical: {
    backgroundColor: red[700],
    animationName: 'pulseUrgencyCritical',
    animationIterationCount: 'infinite',
    animationDuration: '750ms',
  },
  backgroundUrgencyCritical: {
    backgroundColor: red[50],
  },
  buttonUrgencyMedium: {
    backgroundColor: amber[400],
    animationName: 'pulseUrgencyMedium',
    animationIterationCount: 'infinite',
    animationDuration: '750ms',
  },
  buttonResolved: {
    backgroundColor: green[400],
  },
  margin: {
    margin: theme.spacing(0.5),
  },
}));

export interface Properties {
  companyId: string;
}

const AndonMain: FunctionComponent<Properties> = ({ companyId }) => {
  const [open, toggleHelp] = useState(false);

  const { classes, cx } = useStyles();
  const [t] = useTranslation();

  const { tab } = useURLQuery();

  const tabsMeta = [
    {
      key: 'calls',
      label: t(['andon:calls'], { defaultValue: 'Calls' }),
      component: () => <CallsOverview />,
    },
    {
      key: 'schedules',
      label: t(['andon:schedules'], { defaultValue: 'Schedules' }),
      component: () => <Schedules companyId={companyId} />,
    },
    {
      key: 'workers',
      label: t(['andon:workers'], { defaultValue: 'Workers' }),
      component: () => <Workers companyId={companyId} />,
    },
  ];

  const selectedTab = Math.max(
    tabsMeta.findIndex((t) => t.key === tab),
    0,
  );

  const { component: TabContent } = tabsMeta[selectedTab];

  const tabs = useMemo(
    () =>
      tabsMeta.map(({ key, label }) => (
        <Link className={classes.tab} key={key} href={{ query: { tab: key } }}>
          <Tab style={{ width: '100%' }} label={label} />
        </Link>
      )),
    [tabsMeta],
  );

  const helpSteps: Array<HelpSteps> = [
    {
      title: t(['andon:helpTitle'], { defaultValue: 'Andon Help' }),
      component: () => (
        <Trans i18nKey="andon:helpText">
          An Andon call is a call for help. Operators often use this when a line requires a technician to fix an issue,
          hence sometimes refered to as a maintenance call. However, it is not limited to only maintenance calls - you
          can also call managers etc.
          <br />
          All active and resolved Andon call are listed on this page. Click the button to open the call details.
        </Trans>
      ),
    },
    {
      title: t(['andon:supportTypes'], { defaultValue: 'Andon Support Types' }),
      component: () => (
        <>
          <div>
            <Button
              className={cx(classes.buttonUrgencyCritical, classes.buttonMargin)}
              variant="contained"
              color="primary"
            >
              <ReportIcon />
            </Button>
            {t(['andon:helpButtonCriticalExplainText'], {
              defaultValue: 'Critical call wating to be taken.',
            })}
          </div>
          <div>
            <Button
              className={cx(classes.buttonUrgencyMedium, classes.buttonMargin)}
              variant="contained"
              color="primary"
            >
              <ReportIcon />
            </Button>
            {t(['andon:helpButtonMediumExplainText'], {
              defaultValue: 'Call waiting to be taken.',
            })}
          </div>
          <div>
            <Button
              className={cx(classes.buttonInProgress, classes.buttonUrgencyCritical, classes.buttonMargin)}
              variant="contained"
              color="primary"
            >
              <BuildIcon />
            </Button>
            {t(['andon:helpButtonCriticalWaitExplainText'], {
              defaultValue: 'Critical Call has been taken and is in progress of being resolved.',
            })}
          </div>
          <div>
            <Button
              className={cx(classes.buttonInProgress, classes.buttonUrgencyMedium, classes.buttonMargin)}
              variant="contained"
              color="primary"
            >
              <BuildIcon />
            </Button>
            {t(['andon:helpButtonWaitExplainText'], {
              defaultValue: 'Call has been taken and is in progress of being resolved.',
            })}
          </div>
          <div>
            <Button className={cx(classes.buttonResolved, classes.buttonMargin)} variant="contained" color="primary">
              <DoneIcon />
            </Button>
            {t(['andon:helpButtonResolvedExplainText'], {
              defaultValue: 'Call has been resolved.',
            })}
          </div>
        </>
      ),
    },
  ];

  const handleToggleHelp = () => {
    toggleHelp(!open);
  };

  return (
    <ContextTimepickerWrapper>
      <Grid container style={{ marginBottom: 1 / 2 }} spacing={1} alignItems="stretch" justifyContent="flex-start">
        <Grid item xs={12} sm={12} md={12}>
          <Card elevation={1}>
            <CardContent className={classes.headerContainer}>
              <div className={classes.headerText}>
                <Avatar className={classes.avatar}>
                  <RingVolume />
                </Avatar>
                <Typography variant="h5">{t(['andon:pageTitle'], { defaultValue: 'Andon' })}</Typography>

                <IconButton aria-label="Help" className={classes.margin} onClick={handleToggleHelp} size="large">
                  <HelpOutline fontSize="small" />
                </IconButton>

                <HelpDialogStepper
                  title={t(['andon:pageTitle'], { defaultValue: 'Andon' })}
                  steps={helpSteps}
                  open={open}
                  onClose={handleToggleHelp}
                />
              </div>
              <div className={classes.headerTabs}>
                <Tabs
                  indicatorColor="primary"
                  value={selectedTab}
                  // variant={isWidthDown ? 'scrollable' : 'fullWidth'}
                  scrollButtons
                  ScrollButtonComponent={ScrollButton}
                  allowScrollButtonsMobile
                >
                  {tabs}
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {TabContent()}
    </ContextTimepickerWrapper>
  );
};

export default AndonMain;
